/* Track (audio + subtitles) preference persisted for the LEGO player — the
   storage half of the old player's hooks/savedAudioAndSubtitlesHooks.js.

   The old player's `useAudioAndSubtitlesPreference` lives in React-land because
   its UI (TracksControls) also lives there. The lego player's track list and
   selection live in SOLID code, so only the persistence lives here: the shell
   injects `getTrackPreference` / `saveTrackPreference` through the mount
   contract and the solid side does the matching (see LegoSurface). Same
   localStorage key + shape as the old player, so a preference saved by one
   player is picked up by the other. */

/* External */
import { useCallback, useRef } from 'react';

/* Providers */
import { useUserAccount } from '~/providers/UserProvider';

/* Same key as the old player (src/hooks/savedAudioAndSubtitlesHooks.js). */
export const AUDIO_AND_SUBTITLES_PREFERENCE_KEY = 'audioAndSubtitlesPreference';

/* The stored shape:
     { audio?: { iso, isDolby, isDescriptive }, subtitles?: { iso, kind } }
   `subtitles: undefined` (key absent) means "subtitles disabled" and is
   meaningful — it is why the merge below cannot treat an absent field as
   "nothing to change". The old player also wrote a top-level `userId`; we ignore
   it on read and strip it on write (see updateAudioAndSubtitlesPreference). */
function readStoredPreference() {
  try {
    const raw = localStorage.getItem(AUDIO_AND_SUBTITLES_PREFERENCE_KEY);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    /* Corrupt/hand-edited value: behave like an empty store instead of
       throwing inside the player route (the old hook's JSON.parse(null) threw
       here on a fresh install — fixed in the port). */
    return undefined;
  }
}

/* Merge `preference` into the stored value and persist. Mirrors the old
   updateAudioAndSubtitlesPreference: it merges over the STORED object so a
   partial save (e.g. only subtitles) never drops the other track.

   NOTE: unlike the old hook this stores NO `userId`. That id is not stable across
   sessions here, so it cannot scope the entry to a user — and a field that looks
   like a lookup key but is never read is exactly what produced the
   "always falls back to pt / Desativadas" bug. Any `userId` written by the old
   player (or by an earlier build of this port) is dropped on the next save, and
   is ignored when reading. */
export function updateAudioAndSubtitlesPreference(preference) {
  if (!preference) return;
  const { userId: _ignoredUserId, ...stored } = readStoredPreference() ?? {};
  const newPreference = { ...stored, ...preference };
  try {
    localStorage.setItem(
      AUDIO_AND_SUBTITLES_PREFERENCE_KEY,
      JSON.stringify(newPreference),
    );
  } catch {
    /* Private mode / storage disabled: playback must not care. */
  }
}

/* The stored preference. The entry is per DEVICE, not per user.

   ⚠️ Deliberately not keyed on a userId (the old player's
   `stored.userId !== userId → undefined` behaviour). Measured 2026-09: the
   account id is not stable across sessions — an entry written under
   `c093e75b-…` was read by a session whose uid was `359e64f5-…`, so the old gate
   discarded a perfectly valid `{audio:'en', subtitles:{pt}}` on every load. Since
   each session also stamped a new id when saving, that gate could never
   re-converge. Restoring the user's own last choice is the point of the feature;
   on shared TVs the next pick simply overwrites the entry. */
export function getAudioAndSubtitlesPreference() {
  const stored = readStoredPreference();
  if (!stored || typeof stored !== 'object') return undefined;
  const hasAudio = stored.audio !== undefined && stored.audio !== null;
  const hasSubtitles = Object.prototype.hasOwnProperty.call(stored, 'subtitles');
  return hasAudio || hasSubtitles ? stored : undefined;
}

/* Stable ref-backed accessors for the mount contract, so the player never
   remounts when the account or a saved preference changes. */
export default function useLegoTrackPreference() {
  const { uid } = useUserAccount() || {};

  const uidRef = useRef(uid);
  uidRef.current = uid;
  /* The stored value for the CURRENT account, refreshed on every render for as
     long as it could not be read. ⚠️ `useUserAccount()` is undefined while
     UserProvider resolves the id token (UserProvider.jsx: idToken starts
     undefined and is set asynchronously), and the player mounts inside that
     window — so a single read at mount returned "nothing stored" forever, and
     the preference was neither restored nor saved (both are gated on the account
     having resolved). Retrying until a uid exists fixes both, and the retry is
     cheap because it only happens while the account is still loading. */
  const preferenceRef = useRef({ userId: undefined, canRead: false, value: undefined });
  const storedUserId = preferenceRef.current.userId;
  const accountChanged = storedUserId !== uid;
  /* undefined/null userId = the account is still loading: keep the value we have
     and just mark the store as unreadable, so the read retries on a later render.
     (The read does not filter by userId any more — see
     getAudioAndSubtitlesPreference — so this only gates *when* it can read.) */
  const canReadNow = uid !== undefined && uid !== null;
  if (accountChanged || !preferenceRef.current.canRead) {
    preferenceRef.current = {
      userId: uid,
      canRead: canReadNow,
      value: canReadNow
        ? getAudioAndSubtitlesPreference()
        : preferenceRef.current.value,
    };
  }

  /* undefined = "storage not readable yet (no account)", {} = "read, nothing
     saved". The player uses that distinction to know whether it may fall back. */
  const getTrackPreference = useCallback(() => {
    const { canRead, value } = preferenceRef.current;
    if (!canRead) return undefined;
    return value ?? {};
  }, []);

  const saveTrackPreference = useCallback((preference) => {
    /* The uid is not stored (see updateAudioAndSubtitlesPreference), but it still
       marks the moment the account resolved — which is what makes the store
       readable. Before that, nothing has been read yet, so there is nothing
       meaningful to merge into: skip rather than persist a half-known value. */
    if (uidRef.current === undefined || uidRef.current === null) return;
    updateAudioAndSubtitlesPreference(preference);
    preferenceRef.current = {
      userId: uidRef.current,
      canRead: true,
      value: {
        ...preferenceRef.current.value,
        ...preference,
      },
    };
  }, []);

  return { getTrackPreference, saveTrackPreference };
}
